import { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Snitcher diagnostics: verify the paste-in snippet actually loads and boots.
 *
 * Strategy: open the public homepage in a hidden same-origin iframe, wait
 * for it to fire load + a small extra window, then peek at the iframe's
 * `window` for common Snitcher globals (`snitcher`, `sn`, `snPixel`, etc.).
 * If we find one, the snippet is healthy. If not, we distinguish between
 * "snippet not saved yet" and "snippet saved but blocked/failing" so the
 * broker knows where to look.
 *
 * Everything happens client-side against the same-origin preview URL —
 * cookies + auth are irrelevant since the public home is unauthenticated.
 */

const SNITCHER_GLOBALS = ["snitcher", "sn", "snPixel", "snitcherJs", "_sn"];

export default function SnitcherStatus({ settings, brand }) {
  const [state, setState] = useState("idle"); // idle | checking | ok | fail | empty
  const [detail, setDetail] = useState("");
  const [globalFound, setGlobalFound] = useState(null);
  const iframeRef = useRef(null);

  const snippetSet = !!settings?.SNITCHER_SNIPPET?.set;
  const snippetLen = (brand?.snitcher_snippet || "").length;

  const runCheck = () => {
    setState("checking");
    setDetail("");
    setGlobalFound(null);

    if (!snippetSet) {
      setState("empty");
      setDetail("Snippet field is blank — paste the Snitcher <script> block above first.");
      return;
    }

    // Open the public home in a hidden iframe. Same-origin → we can peek
    // into contentWindow safely.
    if (iframeRef.current) {
      try { iframeRef.current.remove(); } catch { /* noop */ }
    }
    const iframe = document.createElement("iframe");
    iframe.src = `/?snitcher_check=1&t=${Date.now()}`;
    iframe.style.cssText = "position:absolute;width:1px;height:1px;left:-9999px;top:-9999px;visibility:hidden";
    iframe.setAttribute("data-testid", "snitcher-check-iframe");
    iframeRef.current = iframe;
    document.body.appendChild(iframe);

    let done = false;
    const finish = (nextState, msg, which = null) => {
      if (done) return;
      done = true;
      setState(nextState);
      setDetail(msg);
      setGlobalFound(which);
      try { iframe.remove(); } catch { /* noop */ }
    };

    iframe.addEventListener("load", () => {
      // Give the snippet a moment to inject its own <script src=…> and boot.
      setTimeout(() => {
        try {
          const w = iframe.contentWindow;
          if (!w) return finish("fail", "Could not access iframe window — same-origin policy blocked introspection.");
          const scripts = w.document.querySelectorAll("script");
          let scriptMounted = false;
          scripts.forEach((s) => {
            if (s.id === "cd-snitcher-script") scriptMounted = true;
          });
          if (!scriptMounted) {
            return finish(
              "fail",
              "The <script id='cd-snitcher-script'> tag was NOT injected. Check that /api/branding is returning the snippet and the frontend isn't blocked by a Content-Security-Policy.",
            );
          }
          const found = SNITCHER_GLOBALS.find((g) => typeof w[g] !== "undefined");
          if (found) {
            return finish("ok", `Snippet loaded and initialized. window.${found} is live.`, found);
          }
          // Script is mounted but the expected global didn't appear — often
          // means an ad-blocker, a wrong install ID, or a Snitcher-side error.
          return finish(
            "fail",
            "Script tag mounted, but none of the expected globals appeared (snitcher / sn / snPixel). " +
              "Common causes: browser ad-blocker, invalid site ID in the snippet, or the Snitcher async loader failed to fetch.",
          );
        } catch (e) {
          return finish("fail", `Introspection threw: ${e.message || e}`);
        }
      }, 3200);
    });

    // Hard timeout fallback in case load never fires.
    setTimeout(() => finish("fail", "Timed out waiting for the homepage iframe to load."), 12000);
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount to avoid orphan iframes if the user navigates away mid-check.
      if (iframeRef.current) { try { iframeRef.current.remove(); } catch { /* noop */ } }
    };
  }, []);

  const badge = (() => {
    if (state === "checking") return { icon: <Loader2 size={12} className="animate-spin" />, text: "Checking…", bg: "#eef2ff", fg: "#4338ca" };
    if (state === "ok") return { icon: <CheckCircle2 size={12} />, text: "Healthy", bg: "#dcfce7", fg: "#166534" };
    if (state === "fail") return { icon: <XCircle size={12} />, text: "Not loading", bg: "#fee2e2", fg: "#991b1b" };
    if (state === "empty") return { icon: <XCircle size={12} />, text: "Not set", bg: "#fef3c7", fg: "#92400e" };
    return { icon: null, text: "Idle", bg: "#f5f5f5", fg: "#525252" };
  })();

  return (
    <div
      className="mt-4 pt-4 border-t border-dashed border-[#eaecf0]"
      data-testid="snitcher-status-panel"
      style={{ fontFamily: "var(--cd-font)" }}
    >
      <div className="text-[10px] uppercase tracking-[0.24em] font-mono text-[#666] mb-3">
        Snitcher status
      </div>

      <div className="rounded-md border border-[#eaecf0] p-3 bg-[#fafbfc]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span
              data-testid="snitcher-status-badge"
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: badge.bg, color: badge.fg }}
            >
              {badge.icon}{badge.text}
            </span>
            {snippetLen > 0 && (
              <span className="text-[11px] font-mono text-[#666]">
                {snippetLen} chars stored
              </span>
            )}
            {globalFound && (
              <span className="text-[11px] font-mono text-[#166534]" data-testid="snitcher-global-found">
                window.{globalFound}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={runCheck}
              disabled={state === "checking"}
              className="cd-btn cd-btn--outline cd-btn--sm"
              data-testid="snitcher-run-check"
              style={{ minHeight: 32 }}
            >
              {state === "checking" ? "Checking…" : "Run diagnostics"}
            </button>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="cd-btn cd-btn--ghost cd-btn--sm"
              title="Open homepage in new tab"
              data-testid="snitcher-open-home"
              style={{ minHeight: 32 }}
            >
              <ExternalLink size={12} strokeWidth={2} />
            </a>
          </div>
        </div>
        {detail && (
          <p
            data-testid="snitcher-detail"
            className="text-[11px] font-mono mt-2"
            style={{ color: state === "ok" ? "#166534" : state === "empty" ? "#92400e" : "#991b1b", lineHeight: 1.55 }}
          >
            {detail}
          </p>
        )}
        {state === "idle" && (
          <p className="text-[11px] font-mono text-[#666] mt-2 leading-relaxed">
            Click <strong>Run diagnostics</strong> to load the public homepage in a hidden
            iframe and verify the Snitcher globals appear. Any ad-blocker in your browser
            will cause a false-negative — try incognito if the check fails but the snippet
            works elsewhere.
          </p>
        )}
      </div>
    </div>
  );
}
